import { onRouteChange } from './history-observer';

export interface ScrollDepthEntry {
  url: string;
  /** Deepest fraction of the page scrolled on this pageview, 0..1. */
  pct: number;
  timestamp: number;
}

/**
 * Track the deepest scroll position reached per pageview and report each new
 * maximum (monotonic, so a handful of events per page). Resets on SPA route
 * changes so each route's depth is measured independently. Powers scrollmaps —
 * the server aggregates the per-(session,url) maxima into a page-level metric.
 */
export function captureScrollDepth(
  callback: (entry: ScrollDepthEntry) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined;

  let currentUrl = window.location.href;
  let maxPct = 0;
  let rafId = 0;

  const measure = (): void => {
    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - window.innerHeight;
    const pct =
      scrollable > 0
        ? Math.min(1, Math.max(0, window.scrollY / scrollable))
        : 1; // page fits in the viewport — fully "seen"
    if (pct > maxPct + 0.01) {
      maxPct = pct;
      callback({ url: currentUrl, pct: maxPct, timestamp: Date.now() });
    }
  };

  const onScroll = (): void => {
    if (rafId) return;
    rafId = window.requestAnimationFrame(() => {
      rafId = 0;
      measure();
    });
  };

  // Reset the per-page measurement on SPA navigation so each route is independent.
  const resetForNewRoute = (): void => {
    if (window.location.href === currentUrl) return;
    currentUrl = window.location.href;
    maxPct = 0;
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  const unsubscribeRoute = onRouteChange(resetForNewRoute);

  return () => {
    window.removeEventListener('scroll', onScroll);
    unsubscribeRoute();
    if (rafId) window.cancelAnimationFrame(rafId);
  };
}
